<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { fade, slide, fly } from "svelte/transition";
  import Settings from "carbon-icons-svelte/lib/Settings.svelte";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import Renew from "carbon-icons-svelte/lib/Renew.svelte";
  import Copy from "carbon-icons-svelte/lib/Copy.svelte";
  import Checkmark from "carbon-icons-svelte/lib/Checkmark.svelte";
  import Search from "carbon-icons-svelte/lib/Search.svelte";
  import Cloud from "carbon-icons-svelte/lib/Cloud.svelte";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";

  import { portal } from "../helpers.js";
  import { fetchServerEnv } from "../data.js";

  const dispatch = createEventDispatcher();

  // Client API URL currently active in build
  const clientApiUrl = typeof TT_WEB_API_URL !== "undefined" ? TT_WEB_API_URL : "";
  const envVarLabel = "TT" + "_WEB_API_URL";

  let serverEnv = null;
  let isLoading = false;
  let errorMessage = "";
  let searchTerm = "";
  let activeTab = "table"; // 'table' | 'json'

  // Copy feedback states
  let copiedClientUrl = false;
  let copiedAllJson = false;
  let copiedRowKey = null;
  let copyTimeout = null;


  onMount(() => {
    loadEnvData();
  });

  async function loadEnvData() {
    isLoading = true;
    errorMessage = "";
    try {
      serverEnv = await fetchServerEnv();
    } catch (err) {
      console.error("[AdminModal] Failed to fetch server environment:", err);
      errorMessage = err.message || "Failed to load environment variables from server.";
    } finally {
      isLoading = false;
    }
  }

  function handleKeydown(e) {
    if (e.key === "Escape") {
      dispatch("close");
    }
  }

  function copyToClipboard(text, type, rowKey = null) {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      if (copyTimeout) clearTimeout(copyTimeout);
      if (type === "clientUrl") copiedClientUrl = true;
      if (type === "allJson") copiedAllJson = true;
      if (type === "row") copiedRowKey = rowKey;

      copyTimeout = setTimeout(() => {
        copiedClientUrl = false;
        copiedAllJson = false;
        copiedRowKey = null;
      }, 2000);
    });
  }

  // Filter env entries by search term
  $: envEntries = serverEnv ? Object.entries(serverEnv) : [];
  $: filteredEntries = envEntries.filter(([k, v]) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    const keyMatch = k.toLowerCase().includes(term);
    const valMatch = String(v).toLowerCase().includes(term);
    return keyMatch || valMatch;
  });

  $: formattedJson = serverEnv ? JSON.stringify(serverEnv, null, 2) : "";
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  use:portal
  class="admin-modal-scrim"
  role="presentation"
  on:click|self={() => dispatch("close")}
  transition:fade={{ duration: 150 }}
>
  <div
    class="admin-modal-card"
    role="dialog"
    aria-modal="true"
    aria-labelledby="admin-dialog-title"
    transition:fly={{ y: 20, duration: 220 }}
  >
    <!-- Modal Header -->
    <header class="admin-header">
      <div class="header-left">
        <div class="admin-icon-badge">
          <Settings size={20} />
        </div>
        <div class="header-titles">
          <div class="title-row">
            <h2 id="admin-dialog-title" class="admin-title">Admin Console</h2>
            <span class="admin-badge">System Diagnostics</span>
          </div>
          <p class="admin-subtitle">Inspect client-side configuration & server-side environment variables</p>
        </div>
      </div>
      <button
        type="button"
        class="admin-close-btn"
        on:click={() => dispatch("close")}
        title="Close (Esc)"
        aria-label="Close dialog"
      >
        <Close size={18} />
      </button>
    </header>

    <!-- Modal Body -->
    <div class="admin-body">
      <!-- Section 1: Client Application API Configuration -->
      <section class="admin-section client-config-section">
        <div class="section-top-bar">
          <div class="section-title-wrap">
            <span class="section-indicator blue"></span>
            <span class="section-title">Client-Side Configuration</span>
            <span class="code-pill">{envVarLabel}</span>
          </div>
          <span class="config-status-badge">Active Client Endpoint</span>
        </div>

        <div class="client-url-box">
          <div class="url-content-row">
            <span class="url-label">Resolved API Base:</span>
            <code class="url-text" title={clientApiUrl}>{clientApiUrl || "Not Defined"}</code>
          </div>
          <button
            type="button"
            class="copy-btn {copiedClientUrl ? 'copied' : ''}"
            on:click={() => copyToClipboard(clientApiUrl, "clientUrl")}
            title="Copy API URL to clipboard"
          >
            {#if copiedClientUrl}
              <Checkmark size={14} />
              <span>Copied!</span>
            {:else}
              <Copy size={14} />
              <span>Copy URL</span>
            {/if}
          </button>
        </div>
      </section>

      <!-- Section 2: Server Environment Variables -->
      <section class="admin-section server-env-section">
        <div class="section-top-bar">
          <div class="section-title-wrap">
            <span class="section-indicator green"></span>
            <span class="section-title">Server Environment Variables</span>
            <span class="endpoint-pill">
              <Cloud size={13} />
              <span>{clientApiUrl}/env</span>
            </span>
          </div>

          <div class="section-actions">
            <!-- View Tabs -->
            <div class="view-toggle-group">
              <button
                type="button"
                class="view-toggle-btn {activeTab === 'table' ? 'active' : ''}"
                on:click={() => (activeTab = 'table')}
              >
                Table View
              </button>
              <button
                type="button"
                class="view-toggle-btn {activeTab === 'json' ? 'active' : ''}"
                on:click={() => (activeTab = 'json')}
              >
                Raw JSON
              </button>
            </div>

            <!-- Reload Button -->
            <button
              type="button"
              class="refresh-btn"
              on:click={loadEnvData}
              disabled={isLoading}
              title="Refresh /env from server"
            >
              <span class="refresh-icon {isLoading ? 'spin' : ''}">
                <Renew size={14} />
              </span>
              <span>{isLoading ? "Fetching..." : "Refresh"}</span>
            </button>
          </div>
        </div>

        <!-- Filter & Control Bar -->
        {#if serverEnv && !errorMessage}
          <div class="env-controls-bar">
            <div class="search-input-wrapper">
              <Search size={15} class="search-icon" />
              <input
                type="text"
                class="search-input"
                placeholder="Search variables by name or value..."
                bind:value={searchTerm}
              />
              {#if searchTerm}
                <button
                  type="button"
                  class="clear-search-btn"
                  on:click={() => (searchTerm = "")}
                  title="Clear search filter"
                >
                  <Close size={13} />
                </button>
              {/if}
            </div>

            <div class="controls-right">
              <span class="variables-count">
                Showing <strong>{filteredEntries.length}</strong> of <strong>{envEntries.length}</strong> variables
              </span>


              <button
                type="button"
                class="copy-all-btn {copiedAllJson ? 'copied' : ''}"
                on:click={() => copyToClipboard(formattedJson, "allJson")}
                title="Copy entire server env as JSON"
              >
                {#if copiedAllJson}
                  <Checkmark size={14} />
                  <span>Copied All!</span>
                {:else}
                  <Copy size={14} />
                  <span>Copy JSON</span>
                {/if}
              </button>
            </div>
          </div>
        {/if}

        <!-- Status: Loading -->
        {#if isLoading && !serverEnv}
          <div class="loading-state-container">
            <div class="loading-spinner"></div>
            <p class="loading-text">Connecting to {clientApiUrl}/env...</p>
            <span class="loading-subtext">Fetching live container environment variables</span>
          </div>

        <!-- Status: Error -->
        {:else if errorMessage}
          <div class="error-banner" transition:slide={{ duration: 150 }}>
            <WarningFilled size={18} class="error-icon" />
            <div class="error-info">
              <strong>Unable to fetch server environment</strong>
              <p>{errorMessage}</p>
              <div class="error-endpoint">Target endpoint: <code>{clientApiUrl}/env</code></div>
            </div>
            <button type="button" class="retry-btn" on:click={loadEnvData}>
              <Renew size={13} />
              <span>Retry</span>
            </button>
          </div>

        <!-- Status: Success - Table View -->
        {:else if activeTab === "table"}
          <div class="env-table-container">
            <table class="env-table">
              <thead>
                <tr>
                  <th style="width: 28%;">Variable Name</th>
                  <th style="width: 62%;">Value</th>
                  <th style="width: 10%; text-align: right;">Action</th>
                </tr>
              </thead>
              <tbody>
                {#if filteredEntries.length === 0}
                  <tr>
                    <td colspan="3" class="empty-results-cell">
                      No environment variables matching "{searchTerm}"
                    </td>
                  </tr>
                {:else}
                  {#each filteredEntries as [key, val] (key)}
                    <tr class="env-row">
                      <td class="env-key-cell">
                        <div class="key-container">
                          <code class="key-code">{key}</code>
                        </div>
                      </td>
                      <td class="env-val-cell">
                        <code class="val-code">
                          {val !== null && val !== undefined ? String(val) : "—"}
                        </code>
                      </td>
                      <td class="env-action-cell">
                        <button
                          type="button"
                          class="row-copy-btn {copiedRowKey === key ? 'copied' : ''}"
                          on:click={() => copyToClipboard(String(val), "row", key)}
                          title="Copy {key} value"
                        >
                          {#if copiedRowKey === key}
                            <Checkmark size={13} />
                          {:else}
                            <Copy size={13} />
                          {/if}
                        </button>
                      </td>
                    </tr>
                  {/each}
                {/if}
              </tbody>
            </table>
          </div>

        <!-- Status: Success - Raw JSON View -->
        {:else}
          <div class="raw-json-container">
            <pre class="json-code-block"><code>{formattedJson}</code></pre>
          </div>
        {/if}
      </section>
    </div>

    <!-- Modal Footer -->
    <footer class="admin-footer">
      <div class="footer-left">
        <span class="footer-pill">TruckTrack Fleet Ops</span>
        <span class="footer-note">Client and Server Diagnostics</span>
      </div>
      <button
        type="button"
        class="admin-done-btn"
        on:click={() => dispatch("close")}
      >
        Done
      </button>
    </footer>
  </div>
</div>

<style>
  /* Scrim & Backdrop */
  .admin-modal-scrim {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.65);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    z-index: 9999;
  }

  /* Modal Surface Card */
  .admin-modal-card {
    width: 860px;
    max-width: 95vw;
    max-height: 88vh;
    background: #ffffff;
    border-radius: 14px;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.2),
      0 10px 10px -5px rgba(0, 0, 0, 0.1),
      0 0 0 1px rgba(226, 232, 240, 0.9);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: #1e293b;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  }

  /* Header */
  .admin-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    border-bottom: 1px solid #e2e8f0;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .admin-icon-badge {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 6px -1px rgba(2, 132, 199, 0.25);
    flex-shrink: 0;
  }

  .header-titles {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .admin-title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.015em;
  }

  .admin-badge {
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #0284c7;
    background: rgba(2, 132, 199, 0.1);
    padding: 2px 7px;
    border-radius: 9999px;
  }

  .admin-subtitle {
    margin: 0;
    font-size: 12.5px;
    color: #64748b;
  }

  .admin-close-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .admin-close-btn:hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  /* Body */
  .admin-body {
    padding: 20px 24px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
  }

  /* Sections */
  .admin-section {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 16px 18px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.03);
  }

  .section-top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 12px;
    flex-wrap: wrap;
  }

  .section-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .section-indicator.blue {
    background-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15);
  }

  .section-indicator.green {
    background-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
  }

  .section-title {
    font-size: 13.5px;
    font-weight: 700;
    color: #1e293b;
  }

  .code-pill {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
    font-weight: 600;
    color: #475569;
    background: #f1f5f9;
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
  }

  .config-status-badge {
    font-size: 11px;
    font-weight: 600;
    color: #0284c7;
    background: #e0f2fe;
    padding: 2px 8px;
    border-radius: 9999px;
  }

  .endpoint-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
    color: #059669;
    background: #ecfdf5;
    padding: 2px 8px;
    border-radius: 6px;
    border: 1px solid #a7f3d0;
  }

  /* Client URL Box */
  .client-url-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8fafc;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 10px 14px;
    gap: 14px;
  }

  .url-content-row {
    display: flex;
    align-items: center;
    gap: 10px;
    overflow: hidden;
  }

  .url-label {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
  }

  .url-text {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12.5px;
    font-weight: 600;
    color: #0369a1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Buttons */
  .copy-btn, .refresh-btn, .retry-btn, .copy-all-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    border: 1px solid transparent;
  }

  .copy-btn {
    background: #ffffff;
    border-color: #cbd5e1;
    color: #334155;
  }

  .copy-btn:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #94a3b8;
  }

  .copy-btn.copied {
    background: #ecfdf5;
    color: #059669;
    border-color: #a7f3d0;
  }

  .section-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .view-toggle-group {
    display: flex;
    background: #f1f5f9;
    border-radius: 6px;
    padding: 2px;
    border: 1px solid #e2e8f0;
  }

  .view-toggle-btn {
    border: none;
    background: transparent;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11.5px;
    font-weight: 500;
    color: #64748b;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .view-toggle-btn.active {
    background: #ffffff;
    color: #0f172a;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .refresh-btn {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #334155;
  }

  .refresh-btn:hover:not(:disabled) {
    background: #ffffff;
    border-color: #0284c7;
    color: #0284c7;
  }

  .refresh-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .refresh-icon.spin {
    display: inline-flex;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* Env Controls Bar */
  .env-controls-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 12px;
    flex-wrap: wrap;
  }

  .search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 240px;
  }

  :global(.search-icon) {
    position: absolute;
    left: 10px;
    color: #94a3b8;
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    height: 32px;
    padding: 0 28px 0 32px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 12px;
    outline: none;
    transition: all 0.15s ease;
  }

  .search-input:focus {
    border-color: #0284c7;
    box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
  }

  .clear-search-btn {
    position: absolute;
    right: 8px;
    border: none;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 2px;
  }

  .clear-search-btn:hover {
    color: #334155;
  }

  .controls-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .variables-count {
    font-size: 11.5px;
    color: #64748b;
  }


  .copy-all-btn {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #475569;
  }

  .copy-all-btn:hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  .copy-all-btn.copied {
    background: #ecfdf5;
    color: #059669;
    border-color: #a7f3d0;
  }

  /* Table View */
  .env-table-container {
    max-height: 380px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }

  .env-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .env-table th {
    position: sticky;
    top: 0;
    background: #f8fafc;
    color: #475569;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 10.5px;
    letter-spacing: 0.04em;
    padding: 9px 12px;
    border-bottom: 1px solid #e2e8f0;
    z-index: 10;
  }

  .env-row {
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.12s ease;
  }

  .env-row:hover {
    background: #f8fafc;
  }


  .env-key-cell {
    padding: 8px 12px;
    vertical-align: top;
  }

  .key-container {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .key-code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11.5px;
    font-weight: 600;
    color: #0f172a;
    background: rgba(2, 132, 199, 0.06);
    padding: 1px 5px;
    border-radius: 4px;
    border: 1px solid rgba(2, 132, 199, 0.12);
  }


  .env-val-cell {
    padding: 8px 12px;
    word-break: break-all;
    vertical-align: top;
  }

  .val-code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11.5px;
    color: #334155;
    white-space: pre-wrap;
    user-select: all;
  }


  .env-action-cell {
    padding: 8px 12px;
    text-align: right;
    vertical-align: top;
  }

  .row-copy-btn {
    width: 26px;
    height: 26px;
    border-radius: 5px;
    border: 1px solid transparent;
    background: transparent;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .row-copy-btn:hover {
    background: #e2e8f0;
    color: #0f172a;
  }

  .row-copy-btn.copied {
    background: #ecfdf5;
    color: #059669;
    border-color: #a7f3d0;
  }

  .empty-results-cell {
    text-align: center;
    padding: 24px;
    color: #64748b;
    font-size: 12.5px;
  }

  /* Raw JSON View */
  .raw-json-container {
    max-height: 380px;
    overflow-y: auto;
    background: #0f172a;
    border-radius: 8px;
    padding: 14px 16px;
    border: 1px solid #1e293b;
  }

  .json-code-block {
    margin: 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    color: #38bdf8;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-all;
  }

  /* Loading State */
  .loading-state-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e2e8f0;
    border-top-color: #0284c7;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 12px;
  }

  .loading-text {
    margin: 0 0 4px;
    font-size: 13.5px;
    font-weight: 600;
    color: #1e293b;
  }

  .loading-subtext {
    font-size: 12px;
    color: #64748b;
  }

  /* Error State */
  .error-banner {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 14px 16px;
    color: #991b1b;
  }

  :global(.error-icon) {
    color: #ef4444;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .error-info {
    flex: 1;
    font-size: 12.5px;
    line-height: 1.4;
  }

  .error-info strong {
    display: block;
    font-size: 13px;
    margin-bottom: 2px;
  }

  .error-info p {
    margin: 0 0 6px;
    color: #b91c1c;
  }

  .error-endpoint code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
    background: rgba(239, 68, 68, 0.1);
    padding: 1px 4px;
    border-radius: 3px;
  }

  .retry-btn {
    background: #ffffff;
    border: 1px solid #fca5a5;
    color: #b91c1c;
    flex-shrink: 0;
  }

  .retry-btn:hover {
    background: #fee2e2;
  }

  /* Footer */
  .admin-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 24px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
  }

  .footer-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .footer-pill {
    font-size: 11.5px;
    font-weight: 700;
    color: #334155;
  }

  .footer-note {
    font-size: 11.5px;
    color: #64748b;
  }

  .admin-done-btn {
    padding: 7px 18px;
    border-radius: 6px;
    border: 1px solid transparent;
    background: #0284c7;
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .admin-done-btn:hover {
    background: #0369a1;
  }
</style>
